import { defineStore } from 'pinia';
import { apiCall } from '@/utils/globalFunctions';
import store from '@/store/store';
import { debounce } from 'lodash';
import { useSubscriptionStore } from '@/store/subscription';

export const useInvoicesStore = defineStore('invoices', {
  state: () => {
    const userId = store.state.user?.user_id || null;
    const storageKey = userId ? `invoiceStore_${userId}` : 'invoiceStore';
    const persistedState = getPersistedState(storageKey);
    return {
      userId,
      invoices: persistedState.invoices || [],
      currentPage: persistedState.currentPage || 1,
      itemsPerPage: persistedState.itemsPerPage || 10,
      totalPages: persistedState.totalPages || 1,
      totalRows: persistedState.totalRows || 0,
      totalInvoice: persistedState.totalInvoice || 0,
      totalPaidInvoice: persistedState.totalPaidInvoice || 0,
      totalOverdueInvoice: persistedState.totalOverdueInvoice || 0,
      totalPendingInvoice: persistedState.totalPendingInvoice || 0,
      searchQuery: '',
      startDate: persistedState.startDate || null,
      endDate: persistedState.endDate || null,
      lastFiveInvoices: [],
      recurringProfiles: persistedState.recurringProfiles || [],
      taxRates: [],
      loading: false,
      error: null,
    };
  },

  getters: {
    filteredInvoices: (state) => state.invoices,
    recurringInvoices: (state) => state.invoices.filter((invoice) => invoice.is_recurring),
    activeRecurringProfiles: (state) => state.recurringProfiles.filter((profile) => profile.is_active),

    filteredTotalInvoice: (state) => state.totalInvoice,
    filteredTotalPaidInvoice: (state) => state.totalPaidInvoice,
    filteredTotalOverdueInvoice: (state) => state.totalOverdueInvoice,
    filteredTotalPendingInvoice: (state) => state.totalPendingInvoice,
  },

  actions: {
    persistState() {
      try {
        setPersistedState(`invoiceStore_${this.userId}`,{
          invoices: this.invoices,
          totalRows: this.totalRows,
          totalInvoice: this.totalInvoice,
          totalPaidInvoice: this.totalPaidInvoice,
          totalOverdueInvoice: this.totalOverdueInvoice,
          totalPendingInvoice: this.totalPendingInvoice,
          recurringProfiles: this.recurringProfiles,
          startDate: this.startDate,
          endDate: this.endDate,
          //searchQuery: this.searchQuery,
          currentPage: this.currentPage,
        });
      } catch (error) {
        console.error('Failed to persist invoices state', error);
      }
    },

    setUserId(userId) {
      this.userId = userId;
      this.reset();
      if (userId) {
        this.fetchInvoices();
      }
    },

    reset() {
      this.$reset();
      if (this.userId) {
        localStorage.removeItem(`invoiceStore_${this.userId}`);
      }
    },

    resetFilters() {
      this.startDate = null;
      this.endDate = null;
      this.searchQuery = '';
      this.currentPage = 1;
    },

    // Debounced fetch function
    _debouncedFetch: debounce(function (params, options) {
      return (async () => {
        const response = await apiCall(
          {
            url: '/Invoice/getInvoices',
            method: options.method,
            dataType: 'json',
            data: params,
          },
          store
        );

        if (response.data && Array.isArray(response.data)) {
          const processedData = response.data.map(invoice => ({
            ...invoice,
            is_active: parseInt(invoice.is_active) || 0,
          }));
          if (options.persist) {
            this.invoices = processedData;
            this.totalPages = response.totalPages || 1;
            this.totalRows = response.totalRows || 0;
            this.persistState();
          }
          return processedData;
        }
        throw new Error(response.message || 'Invalid response format');
      })();
    }, 300),

    // Main private fetch function
    async _fetchData(params = {}, options = {}) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
        return [];
      }

      const defaults = {
        resetFilters: false,
        clearCache: false,
        persist: true,
        debounceTime: 0,
        method: 'GET',
      };

      const finalOptions = { ...defaults, ...options };

      if (finalOptions.clearCache) {
        this.invoices = [];
        this.totalRows = 0;
      }

      if (finalOptions.resetFilters) {
        this.resetFilters();
      }

      this.loading = true;
      this.resetError();

      try {
        if (finalOptions.debounceTime > 0) {
          return await this._debouncedFetch(params, finalOptions);
        } else {
          const response = await apiCall(
            {
              url: '/Invoice/getInvoices',
              method: finalOptions.method,
              dataType: 'json',
              data: params,
            },
            store
          );

          if (response.data && Array.isArray(response.data)) {
            const processedData = response.data.map(invoice => ({
              ...invoice,
              is_active: parseInt(invoice.is_active) || 0,
            }));
            if (finalOptions.persist) {
              this.invoices = processedData;
              this.totalPages = response.totalPages || 1;
              this.totalRows = response.totalRows || 0;
              this.persistState();
            }
            this.totalInvoice = response.totalInvoice || 0;
            this.totalPaidInvoice = response.totalPaidInvoice || 0;
            this.totalOverdueInvoice = response.totalOverdueInvoice || 0;
            this.totalPendingInvoice = response.totalPendingInvoice || 0;
            return processedData;
          }
          throw new Error(response.message || 'Invalid response format');
        }
      } catch (err) {
        this.setError(err, 'Failed to fetch invoices', 'FETCH_INVOICES_FAILED');
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async fetchInvoices(params = {}, options = {}) {
      const {
        page = this.currentPage,
        pageSize = this.itemsPerPage,
        search = this.searchQuery || '',
        startDate = this.startDate || null,
        endDate = this.endDate || null,
      } = params;

      const data = await this._fetchData(
        { startDate, endDate, search, page, pageSize },
        {
          ...options,
          debounceTime: 300, // Default debounce for regular fetches
        }
      );


      this.currentPage = page;
      this.itemsPerPage = pageSize;
      this.searchQuery = search;
      this.startDate = startDate;
      this.endDate = endDate;

      return data;
    },


    async fetchLastFiveInvoices() {
      try {
        const response = await this._fetchData(
          {
            page: 1,
            pageSize: 5
          },
          {
            persist: true,
            debounceTime: 0
          }
        );

        if (response) {
          this.lastFiveInvoices = response;
          return response;
        }

      } catch (err) {
        this.setError(err, 'Failed to fetch last five invoices', 'FETCH_LAST_FIVE_FAILED');
        throw err;
      }
    },

    async fetchTaxRates() {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
        return [];
      }
      if (this.taxRates.length > 0) {
        return this.taxRates;
      }

      try {
        const response = await apiCall({
          url: '/Invoice/getTaxRates',
          method: 'GET'
        }, store);
        
        this.taxRates = response.data || [];
        return this.taxRates;
      } catch (err) {
        this.setError(err, 'Failed to fetch tax rates', 'FETCH_TAX_RATES_FAILED');
        throw err;
      }
    },

    async calculateInvoiceTotals(items) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
      }
      try {
        await this.fetchTaxRates();
        return items.map(item => {
          if (item.tax_id) {
            const taxRate = this.taxRates.find(t => t.tax_id === item.tax_id)?.tax_rate || 0;
            item.tax_amount = (item.quantity * item.unit_price) * (taxRate / 100);
          } else {
            item.tax_amount = 0;
          }
          item.total = (item.quantity * item.unit_price) + item.tax_amount;
          return item;
        });
      } catch (err) {
        console.error('Error calculating invoice totals:', err);
        return items; // Return original items if calculation fails
      }
    },

    async fetchRecurringProfiles() {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
        return [];
      }
      if (this.recurringProfiles.length > 0) {
        return this.recurringProfiles;
      }

      this.loading = true;
      this.resetError();

      try {
        const response = await apiCall({
          url: '/Invoice/getRecurringProfiles',
          method: 'GET',
          dataType: 'json',
        }, store);

        if (response?.data && Array.isArray(response.data)) {
          this.recurringProfiles = response.data;
          this.persistState();
          return response.data;
        }
        throw new Error(response.message || 'Invalid response format');
      } catch (err) {
        this.setError(err, 'Failed to fetch recurring profiles', 'FETCH_RECURRING_FAILED');
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async fetchRecurringProfile(profileId) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
      }
      try {
        const response = await apiCall({
          url: `/Invoice/getRecurringProfile/${profileId}`,
          method: 'GET',
          dataType: 'json',
        }, store);

        if (response.status === 'success' && response.data) {
          const profile = response.data;
          const existingIndex = this.recurringProfiles.findIndex(p => p.profile_id === profileId);

          if (existingIndex === -1) {
            this.recurringProfiles.push(profile);
          } else {
            this.recurringProfiles[existingIndex] = profile;
          }

          this.persistState();
          return profile;
        }
        throw new Error(response.message || 'Failed to fetch recurring profile');
      } catch (err) {
        this.setError(err, 'Failed to fetch recurring profile', 'FETCH_PROFILE_FAILED');
        throw err;
      }
    },

    async populateRecurringData(invoiceData) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
      }
      if (!invoiceData.is_recurring || !invoiceData.recurring_profile_id) {
        return;
      }

      try {
        const profile = await this.fetchRecurringProfile(invoiceData.recurring_profile_id);
        if (profile) {
          invoiceData.frequency = profile.frequency;
          invoiceData.interval_value = profile.interval_value;
          invoiceData.next_run_date = profile.next_occurrence_date;
          invoiceData.end_condition = profile.end_date
            ? 'end_date'
            : profile.end_after_occurrences
            ? 'after_occurrences'
            : 'never';
          invoiceData.end_after_occurrences = profile.end_after_occurrences;
          invoiceData.end_date = profile.end_date;
        }
      } catch (err) {
        console.error('Error populating recurring data:', err);
      }
    },

    async addInvoice(invoiceData) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
      }
      this.loading = true;
      this.resetError();

      try {
        const response = await apiCall({
          method: 'POST',
          url: '/Invoice/addInvoice',
          data: invoiceData,
          dataType: 'json',
        }, store);

        if (response.status === 'success') {
          await this.fetchInvoices();
          useSubscriptionStore().fetchUsage();
          if (invoiceData.is_recurring) {
            await this.fetchRecurringProfiles();
          }
          return response;
        }
        throw new Error(response.message);
      } catch (err) {
        this.setError(err, 'Failed to add invoice', 'ADD_INVOICE_FAILED');
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async updateInvoice(invoiceData) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
      }
      this.loading = true;
      this.resetError();

      try {
        const response = await apiCall({
          method: 'POST',
          url: '/Invoice/updateInvoice',
          data: invoiceData,
          dataType: 'json',
        }, store);

        if (response.status === 'success') {
          await this.fetchInvoices();
          
          if (invoiceData.is_recurring || invoiceData.recurring_profile_id) {
            await this.fetchRecurringProfiles();
          }
          return response;
        }
        throw new Error(response.message);
      } catch (err) {
        this.setError(err, 'Failed to update invoice', 'UPDATE_INVOICE_FAILED');
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async updateInvoiceStatus(invoiceData) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
      }
      try {
        const response = await apiCall({
          method: 'POST',
          url: '/Invoice/updateInvoiceStatus',
          data: invoiceData,
          dataType: 'json',
        }, store);

        if (response.status === 'success') {
          const index = this.invoices.findIndex(i => i.invoice_id === invoiceData.invoice_id);
          if (index !== -1) {
            this.invoices[index].status = invoiceData.status;
            this.persistState();
          }
          return response;
        }
        throw new Error(response.message);
      } catch (err) {
        this.setError(err, 'Failed to update invoice status', 'UPDATE_STATUS_FAILED');
        throw err;
      }
    },

    async deleteRecurringProfile(profileId) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
      }
      try {
        const response = await apiCall({
          method: 'POST',
          url: '/Invoice/deleteRecurringProfile',
          data: { profileId },
          dataType: 'json',
        }, store);

        if (response.status === 'success') {
          
          // Update local state
          const profileIndex = this.recurringProfiles.findIndex(p => p.profile_id === profileId);
          if (profileIndex !== -1) {
            this.recurringProfiles[profileIndex].is_active = 0;
          }

          // Update linked invoice if exists
          const invoiceIndex = this.invoices.findIndex(i => i.recurring_profile_id === profileId);
          if (invoiceIndex !== -1) {
            this.invoices[invoiceIndex].is_recurring = 0;
            this.invoices[invoiceIndex].recurring_profile_id = null;
          }

          this.persistState();
          return response;
        }
        throw new Error(response.message);
      } catch (err) {
        this.setError(err, 'Failed to delete recurring profile', 'DELETE_PROFILE_FAILED');
        throw err;
      }
    },

    async deleteInvoice(invoiceId) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
      }
      try {
        const response = await apiCall({
          method: 'POST',
          url: '/Invoice/deleteInvoice',
          data: { invoiceId },
          dataType: 'json',
        }, store);

        if (response.status === 'success') {
          
          // Check if invoice was recurring before deletion
          const deletedInvoice = this.invoices.find(i => i.invoice_id === invoiceId);
          const wasRecurring = deletedInvoice?.is_recurring;

          // Update local state
          this.invoices = this.invoices.filter(i => i.invoice_id !== invoiceId);
          this.totalRows = Math.max(0, this.totalRows - 1);
          this.totalPages = Math.max(1, Math.ceil(this.totalRows / this.itemsPerPage));

          if (this.invoices.length === 0 && this.currentPage > 1) {
            this.currentPage -= 1;
          }

          this.persistState();

          // Refresh data
          if (this.totalRows > 0) {
            await this.fetchInvoices({ 
              page: this.currentPage, 
              pageSize: this.itemsPerPage, 
              search: this.searchQuery
            });
          }

          if (wasRecurring) {
            await this.fetchRecurringProfiles();
          }

          return response;
        }
        throw new Error(response.message);
      } catch (err) {
        this.setError(err, 'Failed to delete invoice', 'DELETE_INVOICE_FAILED');
        throw err;
      }
    },

    async fetchInvoiceItems(invoiceId) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
        return [];
      }
      try {
        const response = await apiCall({
          method: 'GET',
          url: '/Invoice/getInvoiceItems',
          data: { invoiceId },
          dataType: 'json',
        }, store);

        return response.data || [];
      } catch (err) {
        this.setError(err, 'Failed to fetch invoice items', 'FETCH_ITEMS_FAILED');
        throw err;
      }
    },

    async sendEmail(type, invoiceData) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
      }
      this.loading = true;
      this.resetError();

      try {
        const response = await apiCall({
          method: 'POST',
          url: '/EmailSettings/sendEmail',
          dataType: 'json',
          data: { type, invoiceData }
        }, store);
        //if (response.status === 'success') {
          return response;
        //}
        //throw new Error(response.message);
      } catch (err) {
        this.setError(err, 'Failed to send email', 'SEND_EMAIL_FAILED');
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async refreshInvoices() {
      await this.fetchInvoices();
      await this.fetchRecurringProfiles();
    },

    setSearchQuery(query) {
      this.startDate = null;
      this.endDate = null;
      this.currentPage = 1;
      this.searchQuery = query;
    },

    clearSearch() {
      this.startDate = null;
      this.endDate = null;
      this.searchQuery = '';
      this.currentPage = 1;
      debounce(() => this.fetchInvoices(), 300)(); // Delay fetching invoices after clearing search
    },
    setError(err, fallbackMessage, fallbackCode) {
      this.error = {
        message: err?.message || fallbackMessage,
        code: err?.code || fallbackCode,
        timestamp: new Date().toISOString(),
      };
    },
    resetError() {
      this.error = null;
    },
  },
});

// Helper functions
function getPersistedState(storageKey) {
  try {
    const stored = localStorage.getItem(storageKey);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.warn('Failed to parse stored invoices state', error);
    return {};
  }
}

function setPersistedState(storageKey, state) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to persist invoices state', error);
  }
}